import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useAuth } from "@/hooks/use-auth";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useToast } from "@/hooks/use-toast";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Stethoscope,
  LayoutDashboard,
  FileText,
  Globe,
  MessageSquare,
  Settings as SettingsIcon,
  LogOut,
  Loader2,
  Shield,
  Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SEO } from "@/components/SEO";

const TTL_OPTIONS: { value: string; label: string; ms: number | null }[] = [
  { value: "never", label: "Never", ms: null },
  { value: "1d", label: "1 day", ms: 24 * 60 * 60 * 1000 },
  { value: "1w", label: "1 week", ms: 7 * 24 * 60 * 60 * 1000 },
  { value: "1m", label: "1 month", ms: 30 * 24 * 60 * 60 * 1000 },
  { value: "3m", label: "3 months", ms: 90 * 24 * 60 * 60 * 1000 },
];

function ttlValueFromMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return "never";
  const match = TTL_OPTIONS.find((o) => o.ms === ms);
  return match ? match.value : "never";
}

export default function Settings() {
  const { user, logout, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const [userSynced, setUserSynced] = useState(false);
  const getOrCreateUser = useMutation(api.users.getOrCreateUser);

  useEffect(() => {
    if (user && !userSynced) {
      getOrCreateUser({
        googleId: user.id,
        email: user.email || "",
        name: user.firstName
          ? `${user.firstName} ${user.lastName || ""}`.trim()
          : undefined,
        profileImageUrl: user.profileImageUrl || undefined,
      })
        .then(() => setUserSynced(true))
        .catch((err) => console.error("Failed to sync user with Convex:", err));
    }
  }, [user, userSynced, getOrCreateUser]);

  const convexUser = useQuery(
    api.users.getCurrentUser,
    user && userSynced ? { googleId: user.id } : "skip",
  );
  const isAdmin = useIsAdmin();
  const settings = useQuery(
    api.userSettings.get,
    convexUser && user ? { userId: convexUser._id, googleId: user.id } : "skip",
  );

  const upsertSettings = useMutation(api.userSettings.upsert);
  const requestSweep = useMutation(api.userSettings.requestSweep);

  const [pendingTtl, setPendingTtl] = useState<{
    value: string;
    ms: number | null;
  } | null>(null);
  const [eligibleCount, setEligibleCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [exportingTemplates, setExportingTemplates] = useState(false);

  const eligibleResult = useQuery(
    api.userSettings.countEligibleForDeletion,
    convexUser && user && pendingTtl && pendingTtl.ms !== null
      ? {
          userId: convexUser._id,
          googleId: user.id,
          retentionTtlMs: pendingTtl.ms,
        }
      : "skip",
  );

  useEffect(() => {
    if (eligibleResult !== undefined) {
      setEligibleCount(eligibleResult);
      setLoadingCount(false);
    }
  }, [eligibleResult]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) {
    window.location.href = "/api/login";
    return null;
  }
  if (!userSynced || !convexUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const currentView = settings?.defaultDocumentView ?? "markdown";
  const currentTtlValue = ttlValueFromMs(settings?.retentionTtlMs ?? null);
  const learnFromEdits = settings?.learnFromEdits ?? false;

  const errorMessage = (err: unknown): string => {
    if (err instanceof Error) return err.message;
    return "Try again.";
  };

  const handleLearnFromEditsChange = async (next: boolean) => {
    try {
      await upsertSettings({
        userId: convexUser._id,
        googleId: user.id,
        learnFromEdits: next,
      });
      toast({
        title: "Saved",
        description: next
          ? "Learn from Edits is on. Your edits will refine your templates."
          : "Learn from Edits is off.",
      });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: errorMessage(err),
        variant: "destructive",
      });
    }
  };

  const handleViewChange = async (next: string) => {
    if (next !== "markdown" && next !== "plain") return;
    try {
      await upsertSettings({
        userId: convexUser._id,
        googleId: user.id,
        defaultDocumentView: next,
      });
      toast({ title: "Saved", description: "Default document view updated." });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: errorMessage(err),
        variant: "destructive",
      });
    }
  };

  const handleTtlChange = (next: string) => {
    const opt = TTL_OPTIONS.find((o) => o.value === next);
    if (!opt) return;
    if (next === currentTtlValue) return;
    if (opt.ms === null) {
      void saveTtl(null);
      return;
    }
    const previousMs = settings?.retentionTtlMs ?? null;
    if (previousMs !== null) {
      void saveTtl(opt.ms);
      return;
    }
    setPendingTtl({ value: opt.value, ms: opt.ms });
    setEligibleCount(null);
    setLoadingCount(true);
  };

  const saveTtl = async (ms: number | null) => {
    try {
      await upsertSettings({
        userId: convexUser._id,
        googleId: user.id,
        retentionTtlMs: ms,
      });
      if (ms !== null) {
        await requestSweep({ userId: convexUser._id, googleId: user.id });
        toast({
          title: "Saved",
          description: "Auto-cleanup updated. A sweep has been started.",
        });
      } else {
        toast({
          title: "Saved",
          description: "Auto-cleanup turned off. No further deletions will run.",
        });
      }
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: errorMessage(err),
        variant: "destructive",
      });
    }
  };

  const confirmTtl = async () => {
    if (!pendingTtl) return;
    const ms = pendingTtl.ms;
    setPendingTtl(null);
    setEligibleCount(null);
    await saveTtl(ms);
  };

  const pendingLabel =
    TTL_OPTIONS.find((o) => o.value === pendingTtl?.value)?.label || "";

  const handleExportTemplates = async () => {
    if (exportingTemplates) return;
    setExportingTemplates(true);
    try {
      const response = await fetch("/api/templates/export", {
        credentials: "include",
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as
          | { error?: string }
          | null;
        throw new Error(payload?.error || "Failed to export templates");
      }
      const exported = await response.json() as unknown[];
      const json = JSON.stringify(exported, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lrscribe-templates-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast({
        title: "Templates exported",
        description: `${exported.length} template${exported.length === 1 ? "" : "s"} downloaded.`,
      });
    } catch (err) {
      toast({
        title: "Couldn't export templates",
        description: errorMessage(err),
        variant: "destructive",
      });
    } finally {
      setExportingTemplates(false);
    }
  };

  return (
    <div className="h-screen flex flex-col bg-background">
      <SEO title="Settings — LRScribe" noindex />
      <header className="relative h-[26px] border-b border-border bg-card flex items-center justify-between px-2 shrink-0 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Link
            href="/dashboard"
            className="flex items-center gap-1 shrink-0"
            aria-label="LRScribe home"
          >
            <div className="p-0.5 bg-primary/10 rounded-sm">
              <Stethoscope className="w-3 h-3 text-primary" />
            </div>
            <span className="font-heading font-bold text-xs text-foreground hidden sm:inline leading-none">
              LRScribe
            </span>
          </Link>
        </div>

        <nav className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
          <Link
            href="/dashboard"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-dashboard"
          >
            <LayoutDashboard className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <Link
            href="/templates"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-templates"
          >
            <FileText className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Templates</span>
          </Link>
          <Link
            href="/rules"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-rules"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Rules</span>
          </Link>
          <Link
            href="/chat"
            className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1 leading-none"
            data-testid="link-chat"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Chat</span>
          </Link>
        </nav>

        <div className="flex items-center gap-0.5 shrink-0">
          <ThemeToggle className="h-5 w-5 [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Open user menu"
                className="flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background cursor-pointer"
                data-testid="button-user-menu"
              >
                <Avatar className="h-5 w-5 pointer-events-none">
                  <AvatarImage src={user.profileImageUrl || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px]">
                    {user.firstName?.charAt(0) || user.email?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <div
                  className="text-sm font-medium text-foreground truncate"
                  data-testid="text-user-name"
                >
                  {user.firstName
                    ? `${user.firstName} ${user.lastName || ""}`.trim()
                    : user.email}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link href="/settings" data-testid="link-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link href="/security" data-testid="link-security">
                  <Shield className="w-4 h-4 mr-2" />
                  Security
                </Link>
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem asChild>
                  <Link href="/admin/settings" data-testid="link-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => logout()}
                className="text-destructive focus:text-destructive"
                data-testid="button-logout"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-6 py-10 space-y-10">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Settings</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Preferences that apply to your whole account.
            </p>
          </div>

          <section className="space-y-4" data-testid="section-documents">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Documents
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Choose how generated documents are shown by default.
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-card">
              <Label className="text-sm font-medium">
                Default document view
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Generated documents open in this view. You can still switch
                views on individual documents.
              </p>
              <Select
                value={currentView}
                onValueChange={handleViewChange}
              >
                <SelectTrigger
                  className="w-56"
                  data-testid="select-default-document-view"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="markdown" data-testid="option-view-markdown">
                    Markdown
                  </SelectItem>
                  <SelectItem value="plain" data-testid="option-view-plain">
                    Plain text
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="space-y-4" data-testid="section-ai">
            <div>
              <h2 className="text-lg font-semibold text-foreground">AI</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Let AI improve your templates from how you use them.
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-card flex items-start justify-between gap-4">
              <div className="flex-1">
                <Label className="text-sm font-medium" htmlFor="switch-learn-from-edits">
                  Learn from Edits
                </Label>
                <p className="text-xs text-muted-foreground mt-1">
                  When you (or an agent) edit a generated section, the template's
                  instructions, examples, or formatting are refined automatically
                  so future documents reflect that change.
                </p>
              </div>
              <Switch
                id="switch-learn-from-edits"
                checked={learnFromEdits}
                onCheckedChange={handleLearnFromEditsChange}
                data-testid="switch-learn-from-edits"
              />
            </div>
          </section>

          <section className="space-y-4" data-testid="section-retention">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Storage & Retention
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Automatically remove older items to keep your account tidy.
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-card">
              <Label className="text-sm font-medium">Auto-cleanup</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Recordings, audio, and generated documents older than this will
                be permanently deleted. This cannot be undone.
              </p>
              <Select
                value={currentTtlValue}
                onValueChange={handleTtlChange}
              >
                <SelectTrigger
                  className="w-56"
                  data-testid="select-retention-ttl"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TTL_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`option-ttl-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          <section className="space-y-4" data-testid="section-account-data">
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                Account data
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Download a portable copy of the templates you own.
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-card flex items-center justify-between gap-4">
              <div>
                <Label className="text-sm font-medium">Templates</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Includes sections, examples, formatting, profiles, tags, and rules.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleExportTemplates}
                disabled={exportingTemplates}
                data-testid="button-export-templates"
              >
                {exportingTemplates ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {exportingTemplates ? "Exporting…" : "Export Templates"}
              </Button>
            </div>
          </section>
        </div>
      </div>

      <AlertDialog
        open={!!pendingTtl}
        onOpenChange={(open) => {
          if (!open) {
            setPendingTtl(null);
            setEligibleCount(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on auto-cleanup?</AlertDialogTitle>
            <AlertDialogDescription>
              {loadingCount ? (
                <span>Checking how many items would be deleted…</span>
              ) : (
                <>
                  Items older than{" "}
                  <span className="font-medium text-foreground">
                    {pendingLabel}
                  </span>{" "}
                  will be permanently deleted.{" "}
                  {eligibleCount !== null && eligibleCount > 0 ? (
                    <span>
                      <span className="font-medium text-foreground">
                        {eligibleCount}
                      </span>{" "}
                      existing item{eligibleCount === 1 ? "" : "s"} match this
                      cutoff and will be deleted now. This also affects new
                      items as they age past the threshold.
                    </span>
                  ) : (
                    <span>
                      No existing items match this cutoff yet, but new items
                      will be deleted as they age past it.
                    </span>
                  )}{" "}
                  Deletion is permanent.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-ttl">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmTtl}
              data-testid="button-confirm-ttl"
              disabled={loadingCount}
            >
              Turn on
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
